import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Divider,
} from '@mui/material';
import axios from "axios";
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import { Link } from "react-router-dom";
import { USER_ACTIONS, isAllowedAction } from "../config/auth";
import { PARTICIPANT_DOCUMENTS, PARTICIPANT_RETRIEVE_ACTUAL_DOCUMENT } from "../config/settings";
import DownloadIcon from "@mui/icons-material/Download";
import { useKeycloak } from "@react-keycloak/web";
import { messages } from '../config/messages';

const ParticipantDetailsDialog = ({ open, onClose, participant, onEdit, mappings, schemaAsObject }) => {
    const [documents, setDocuments] = useState([{ title: "", description: "", file: null, id: null }]);
    const [participantId, setParticipantId] = useState(null);
    const { keycloak } = useKeycloak();

    useEffect(() => {
        if (participant?.id) {
            setParticipantId(participant.id);
        }
    }, [participant]);

    useEffect(() => {
        if (participantId) {
            fetchDocuments();
        }
    }, [participantId]);

    const fetchDocuments = async () => {
        try {
            const res = await axios.get(PARTICIPANT_DOCUMENTS, {
                params: { participant: participantId }
            });
            if (Array.isArray(res.data)) {
                const filteredDocs = res.data; //.filter(doc => doc.participant === participantId);
                if (filteredDocs.length > 0) {
                    setDocuments(filteredDocs);
                } else {
                    setDocuments([{ title: "", description: "", file: null, id: null }]);
                }
            }
        } catch (err) {
            console.error("Error fetching documents:", err);
        }
    };

    if (!participant) return null;
    // Function to render values based on their type
    // console.log(participant, mappings, schemaAsObject);
    const renderValue = (value) => {
        // 1. Handle arrays
        if (Array.isArray(value)) {
          return value.length > 0
            ? value.map((item, index) => (
                <Typography key={index} variant="body1">
                  {item.value} ({item.language})
                </Typography>
              ))
            : <Typography variant="body1">{messages.participantDetailsNA}</Typography>;
        }

        // 2. Handle boolean explicitly
        if (typeof value === "boolean") {
          return (
            <Typography variant="body1">{value ? "Yes" : "No"}</Typography>
          );
        }

        // 3. Handle null / undefined / empty
        if (value === null || value === undefined || value === "") {
          return (
            <Typography variant="body1">{messages.participantDetailsNA}</Typography>
          );
        }

        // 4. Default
        return <Typography variant="body1">{value}</Typography>;
      };

    const renderLabel = (value, vocab) => {
        if (Array.isArray(value)) {
            return value.length > 0 ? value.map((item, index) => {
                // Find the matching vocab object by value
                const vocabItem = vocab.find(v => v.value === item.value);
                return (
                    <Typography key={index} variant="body1">
                        {vocabItem ? vocabItem.label : messages.participantDetailsNA} ({item.language})
                    </Typography>
                );
            }) : <Typography variant="body1">{messages.participantDetailsNA}</Typography>;
        }
        // Handle single values (not arrays)
        const vocabItem = vocab.find(v => v.value === value);
        return <Typography variant="body1">{vocabItem ? vocabItem.label : messages.participantDetailsNA}</Typography>;
    };

    const handleDownload = async (url, fileName) => {
        try {
            const res = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${keycloak.token}`,
                },
            });
            if (!res.ok) throw new Error("Failed to download");

            const blob = await res.blob();
            const downloadLink = document.createElement("a");
            downloadLink.href = window.URL.createObjectURL(blob);
            downloadLink.download = fileName;
            downloadLink.click();
        } catch (err) {
            console.error("Download failed:", err);
            alert("Failed to download file.");
        }
    };


    const renderFileSection = (documents) => {
        return documents.map((doc, index) => {
            if (typeof doc.file === "string") {
                const fileName = decodeURIComponent(doc.file.split("/").pop());
                // const encodedFileName = encodeURIComponent(fileName);
                const downloadUrl = PARTICIPANT_RETRIEVE_ACTUAL_DOCUMENT+ "participant_"+`${participantId}/`+`${fileName}`;
                // console.log(downloadUrl);
                return (
                    <Box  key={index} sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                        <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                            {fileName.length > 40 ? `${fileName.slice(0, 37)}...` : fileName}
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownload(downloadUrl, fileName)}
                        >{messages.documentDownloadButton}</Button>
                    </Box>
                );
            }
            return null; // Return null for non-string files to avoid warning
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth={"lg"}>
            <DialogTitle><Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
                {`${messages.participantCardTitle} ${participant.name[0]?.value || 'N/A'}`}
            </Typography></DialogTitle>
            <DialogContent>
                <Grid container spacing={2} direction="row">
                    <Grid item container size={12}>
                        <Grid item size={6}>
                            <Stack spacing={1}>
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>{messages.participantDetailsID}</Typography>
                                {renderValue(participant.id)}
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>{messages.participantDetailsCreatedAt}</Typography>
                                {renderValue(participant.management.created_at)}
                            </Stack>

                            <Stack spacing={2} sx={{ pt: 2 }}>
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>{messages.documentUploadAreaTitle}</Typography>
                                {renderFileSection(documents)}
                                {!isAllowedAction(USER_ACTIONS.UPDATE_OWN_CONNECTOR) && <Button component={Link}
                                    variant="outlined"
                                    sx={{ textAlign: "center" }}
                                    to={{
                                        pathname: `/participant-documents/${participantId}`,
                                        state: { participant }
                                    }}>
                                    {messages.participantLandingPageGoToDocumentsButton}
                                </Button>}
                            </Stack>
                        </Grid>
                        <Grid item size={6} sx={{ p: 1, backgroundColor: "#eef2f6", borderRadius: "1em" }}>
                            <Stack spacing={1}>
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>{messages.participantDetailsApproveStatus}</Typography>
                                {renderValue(participant.management.approval_status)}
                                {participant.management.approved_at && <>
                                    <Typography variant="h5" sx={{ fontWeight: 600 }}>{messages.participantDetailsApprovedAt}</Typography>
                                    {renderValue(participant.management.approved_at)}
                                </>
                                }
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>{messages.participantDetailsNameOptIn}</Typography>
                                {participant.management.opt_out_central_catalogue === false ?
                                <Typography variant="body1">Yes</Typography> :
                                <Typography variant="body1">No</Typography>}
                                <Divider/>
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>{messages.EUControlledValidatedLabel}</Typography>
                                {(participant?.management?.validated_eu_controlled === true) ?
                                    <Typography variant="body1">Yes</Typography> :
                                    <Typography variant="body1">No</Typography>}
                                <Divider/>
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>{messages.LLMs4EUValidatedLabel}</Typography>
                                {(participant?.management?.llms4eu_partner === true) ?
                                    <Typography variant="body1">Yes</Typography> :
                                    <Typography variant="body1">No</Typography>}
                            </Stack>
                        </Grid>

                    </Grid>
                    <Grid item container size={12} sx={{pt: 1}}>
                        <Grid item size={6}>
                            <Stack spacing={1}>
                               {participant.address?.street_address.length > 0 ? (
                                    <>
                                        {renderValue(participant.address.street_address)}
                                        {renderValue(participant.address.administrative_area)}
                                        {renderValue(participant.address.city)}
                                        {renderValue(participant.address.postal_code)}
                                        {renderLabel(participant.address.country, mappings.Country.vocab)}
                                    </>) : (<Typography variant="body1">{messages.participantDetailsNA}</Typography>)
                                }
                                <Typography variant="h5"  sx={{fontWeight: 600}}>{schemaAsObject.properties.other_address.title}</Typography>
                                {participant.other_address?.street_address.length > 0 ? (
                                    <>
                                        {renderValue(participant.other_address.street_address)}
                                        {renderValue(participant.other_address.administrative_area)}
                                        {renderValue(participant.other_address.city)}
                                        {renderValue(participant.other_address.postal_code)}
                                        {renderLabel(participant.other_address.country, mappings.Country.vocab)}
                                    </>) : (<Typography variant="body1">N/A</Typography>)
                                }
                                <Typography variant="h5" sx={{fontWeight: 600}}>{schemaAsObject.properties.legal_representative.title}</Typography>
                                {participant?.legal_representative ? (
                                    <>
                                        {renderValue(participant.legal_representative.name)}
                                        {participant.legal_representative?.email &&
                                            <Typography variant="body1">
                                                {participant.legal_representative.email}
                                            </Typography>
                                        }
                                    </>) : (<Typography variant="body1">N/A</Typography>)
                                }
                                <Typography variant="h5" sx={{fontWeight: 600}}>{schemaAsObject.properties.technical_contact.title}</Typography>
                                {participant?.technical_contact ? (
                                    <>
                                        {renderValue(participant.technical_contact.name)}
                                        {participant.technical_contact?.email &&
                                            <Typography variant="body1">
                                                {participant.technical_contact.email}
                                            </Typography>
                                        }
                                    </>) : (<Typography variant="body1">N/A</Typography>)
                                }

                                <Typography variant="h5" sx={{ fontWeight: 600 }}>{schemaAsObject.properties.activities.title}</Typography>
                                {participant.activities && participant.activities.length > 0 ? participant.activities.map((activity, index) => (
                                    <div key={index}>
                                        {activity.map((item, subIndex) => (
                                            <Typography key={subIndex} variant="body1">{item.value} ({item.language})</Typography>
                                        ))}
                                    </div>
                                )) : <Typography variant="body1">N/A</Typography>}
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>{schemaAsObject.properties.description.title}</Typography>
                                {renderValue(participant.description)}

                            </Stack>
                        </Grid>
                        <Grid item size={6}>
                            <Stack spacing={1}>
                                <Stack spacing={1} sx={{ p: 1, backgroundColor: "#FDF9D7", borderRadius: "1em" }}>
                                    <Typography variant="h5" sx={{ fontWeight: 600 }}>{schemaAsObject.properties.pic_number.title}</Typography>
                                    {renderValue(participant.pic_number)}
                                    <Typography variant="h5" sx={{ fontWeight: 600 }}>{schemaAsObject.properties.oca_project.title}</Typography>
                                    {renderValue(participant.oca_project)}
                                    <Typography variant="h5" sx={{ fontWeight: 600 }}>{schemaAsObject.properties.eu_controlled_entity.title}</Typography>
                                    {renderValue(participant.eu_controlled_entity)}
                                </Stack>

                                <Typography variant="h5" sx={{ fontWeight: 600 }}>{schemaAsObject.properties.address.title}</Typography>

                                <Typography variant="h5" sx={{fontWeight: 600}}>{schemaAsObject.properties.short_name.title}</Typography>
                                {renderValue(participant.short_name)}
                                <Typography variant="h5" sx={{fontWeight: 600}}>{schemaAsObject.properties.vat.title}</Typography>
                                {renderValue(participant.vat)}
                                <Typography variant="h5" sx={{fontWeight: 600}}>{schemaAsObject.properties.eu_id.title}</Typography>
                                {renderValue(participant.eu_id)}
                                <Typography variant="h5" sx={{fontWeight: 600}}>{schemaAsObject.properties.organization_type.title}</Typography>
                                {renderValue(participant.organization_type)}
                                <Typography variant="h5" sx={{fontWeight: 600}}>{schemaAsObject.properties.company_type.title}</Typography>
                                {renderLabel(participant.company_type, mappings.CompanyType.vocab)}
                                <Typography variant="h5" sx={{fontWeight: 600}}>{schemaAsObject.properties.location_of_incorporation.title}</Typography>
                                {renderLabel(participant.location_of_incorporation, mappings.Country.vocab)}
                                <Typography variant="h5" sx={{fontWeight: 600}}>{schemaAsObject.properties.identifier.title}</Typography>
                                {participant.identifier.length > 0 ? (
                                    <>
                                        {participant.identifier.map((item, index) => (<>
                                            <Typography key={index} variant="body1">
                                                <a href={item.identifier_notation} target="_blank" rel="noopener noreferrer">{item.identifier_notation}</a>
                                            </Typography>
                                            {renderValue(item.creator)}
                                        </>
                                        ))}
                                    </>
                                ) : (<Typography variant="body1">N/A</Typography>)
                                }
                            </Stack>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                {isAllowedAction(USER_ACTIONS.ADMINISTER_PARTICIPANT_REGISTRY) &&
                    <Button onClick={() => onEdit(participant)}
                        disabled={["WITHDRAWN"].includes(participant?.management?.approval_status)}>
                        Edit participant</Button>}
                {isAllowedAction(USER_ACTIONS.UPDATE_OWN_FORM) && <Button onClick={() => onEdit(participant)}
                    disabled={["WITHDRAWN"].includes(participant?.management?.approval_status)}>Edit my info</Button>}
            </DialogActions>
        </Dialog>
    );
};

export default ParticipantDetailsDialog;
